import { useEffect, useRef } from "react";

const TITLE_ID = "lane-effetti-confirm-title";

export type AzioneLane = "accendi" | "esegui";

/**
 * Conferma per le lane che agiscono FUORI da OM (oggi: l'invio ordini ad
 * AllService). Nate spente apposta, ma senza questo passaggio bastava un clic
 * su «Esegui ora» — che non guarda nemmeno il toggle — per mandare la mail
 * d'ordine vera al fornitore, fino a 50 ordini in un colpo. Le righe della
 * lista sono tutte uguali e il bottone sta sempre nello stesso punto: il clic
 * sulla riga sbagliata non è un'ipotesi.
 *
 * Stesso pattern modale di ``CarrierSwitchConfirmDialog``: role=dialog,
 * overlay cliccabile, Esc, fuoco iniziale su Annulla (mai sull'azione che
 * agisce).
 */
export function LaneEffettiConfirmDialog({
  nome,
  effetto,
  azione,
  onConfirm,
  onCancel,
}: {
  nome: string;
  effetto: string;
  azione: AzioneLane;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const eseguiSubito = azione === "esegui";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
            {eseguiSubito ? `Eseguire subito «${nome}»?` : `Accendere «${nome}»?`}
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-2">
            Questa sincronizzazione agisce fuori da Order Manager.
          </p>
        </header>

        <div className="px-5 py-4 text-[12.5px] leading-relaxed text-ink">
          <p>{effetto}</p>
          <p className="mt-2 text-ink-2">
            {eseguiSubito
              ? "Parte adesso, una sola volta, anche se l'interruttore è spento."
              : "Da qui in poi partirà da sola a ogni intervallo, finché non la rispegni."}
          </p>
        </div>

        <footer className="border-t border-line px-5 py-3">
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              data-testid="lane-effetti-cancel-btn"
              onClick={onCancel}
              className="rounded-full border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper"
            >
              Annulla
            </button>
            <button
              type="button"
              data-testid="lane-effetti-confirm-btn"
              onClick={onConfirm}
              className="rounded-full border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2"
            >
              {eseguiSubito ? "Esegui adesso" : "Accendi"}
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
